<template>
  <div>
    <b-modal
      ref="taskdetail"
      id="taskdetail"
      @hidden="modalClosed"
      hide-footer
      hide-header
      :size="getModalSize"
      :title="item.title"
      :no-close-on-backdrop="noCloseOnBackdrop"
      body-class="modal-body-wo"
      @hide="modalHiding"
    >
      <div :class="{ 'archived-bi-details': readOnly }">
        <h2 v-if="readOnly" :class="{ 'striped-header': readOnly }">
          <span v-if="item.isReadonly">Readonly</span>
          <span v-if="item.isArchived">Archived</span>
        </h2>

        <div>
          <ValidationObserver tag="form" ref="form" @submit.stop.prevent="() => {}" v-slot="{ invalid }">
            <div v-if="loading">
              <div class="d-flex align-items-center justify-content-center p-5">
                <loading-spinner></loading-spinner>
              </div>
            </div>
            <modal-window v-if="!loading" class="taskdetail" :main-width="9" @dragenter="dragEnter" @dragleave="dragLeave">
              <div>
                <ValidationProvider name="BoardItemTitle" rules="required" v-slot="{ errors }">
                  <h1>
                    <textarea-autosize
                      class="area task-title"
                      rows="1"
                      v-on:keydown.enter.native="$event.target.blur()"
                      v-model="item.title"
                      ref="title"
                      placeholder="Click here to enter the title"
                      :min-height="20"
                      v-on:blur.native="autoSaveDelayed"
                      :disabled="readOnly"
                    />
                  </h1>
                  <div v-if="errors[0]" class="board-item-title-error alert alert-danger" role="alert">Title is required</div>
                </ValidationProvider>

                <div v-if="item.isSystemGenerated" class="label">Generated by System</div>

                <BoardItemLabels :item="item" :isEditable="!readOnly" />

                <div class="label mt-3 mb-1">Task Category / Type</div>

                <ValidationProvider name="BoardItemCategory" rules="required" v-slot="{ errors }">
                  <grouped-select
                    :options="availableBoardCategories"
                    v-model="item.boardItemTypeId"
                    :disabled="item.isSystemGenerated || readOnly"
                  ></grouped-select>

                  <div v-if="errors[0]" class="alert alert-danger" role="alert">Category / Type is required</div>
                </ValidationProvider>

                <BoardItemConnections :boardItem="item" v-if="!item.isTemplate"></BoardItemConnections>

                <div class="label mt-4 mb-1">
                  Description
                  <i class="far fa-pencil" v-if="!readOnly" @click="startEditDescription()"></i>
                </div>

                <div class="description" v-if="!editdescription" v-html="linkifiedDescription" @click="startEditDescription"></div>

                <div class="description" v-if="!item.description && !editdescription" @click="startEditDescription()">No description</div>

                <ValidationProvider name="BoardItemDescription">
                  <wysiwig-editor
                    :simple-mode="!item.isTemplate"
                    ref="editor"
                    v-show="editdescription"
                    v-model="item.description"
                    v-on:imagepasted="imagePasted"
                    v-on:blur="leaveDescription('blur')"
                  ></wysiwig-editor>
                  <div class="d-flex justify-content-end" v-if="editdescription">
                    <button class="btn btn-sm btn-success mt-2" @click="leaveDescription('click')">
                      <i class="far fa-save"></i>
                      Save description
                    </button>
                  </div>
                </ValidationProvider>

                <div class="mt-4 mb-1" v-if="item.spreadsheetData || showBoardItemSpreadsheet">
                  <BoardItemSpreadsheet
                    :json="item.spreadsheetData"
                    :sheet-name="'Flight Changes'"
                    @modelUpdate="processSpreadsheetUpdate"
                    @spreadsheetDelete="processSpreadsheetDelete"
                  />
                </div>

                <!-- <BoardItemDelayInfo /> -->

                <WideAttachments :boardItem="item" />

                <!-- Checklist section -->
                <Checklist :item="item" :isTemplate="item.isTemplate" :readOnly="readOnly"></Checklist>

                <div v-if="showAttachmentsSection">
                  <AttachmentView
                    :destinationId="item.id"
                    :destination="'BoardItem'"
                    :attachments="orderedAttachments"
                    :readOnly="readOnly"
                    :ignore-file-extensions="'eml'"
                  />

                  <AttachmentView
                    :destinationId="item.id"
                    :destination="'BoardItem'"
                    :attachments="orderedAttachments"
                    :readOnly="readOnly"
                    :header="'Sent and received Emails'"
                    :fileExtensions="'eml'"
                    :disable-download-all="true"
                  />
                </div>

                <div v-if="!item.isTemplate && item.actions && item.actions.length">
                  <BoardItemActions :item="item"></BoardItemActions>
                </div>

                <div v-if="item.isTemplate" class="w-100">
                  <div class="label mt-2 mb-1">Actions</div>
                  <p>Optional - Please refer to documentation for format</p>

                  <ValidationProvider name="BoardItemActionsJson">
                    <v-jsoneditor v-model="actionsTemplateObj" :options="jsonEditorOptions" inputmode="code" height="400px"></v-jsoneditor>
                  </ValidationProvider>
                </div>

                <div v-if="item.isTemplate" class="w-100 mt-4">
                  <div class="label mt-2 mb-1">Dynamic files</div>
                  <p>Optional - Please refer to documentation for format</p>

                  <ValidationProvider name="BoardItemDynamicTemplateFiles">
                    <v-jsoneditor
                      v-model="dynamicTemplateFilesObj"
                      :options="jsonEditorOptions"
                      inputmode="code"
                      height="200px"
                    ></v-jsoneditor>
                  </ValidationProvider>
                </div>
              </div>

              <task-detail-comments v-model="commentIsDirty"></task-detail-comments>

              <template v-slot:dropzone>
                <!-- Dropzone for File attachments -->
                <div
                  v-if="draggedOver"
                  class="dropzone w-100 d-flex flex-col align-items-center justify-content-center"
                  style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; z-index: 100"
                  :class="{ 'dropzone-dragged': dragOverDropzone }"
                  @drop.prevent="dropDropzone"
                  @dragenter.prevent="dragEnterDropzone"
                  @dragleave.prevent="dragLeaveDropzone"
                  @dragend.prevent
                  @dragover.prevent
                >
                  <div
                    class="text-2xl font-weight-bold drop-zone-text"
                    style="color: #888"
                    @drop.prevent="dropDropzone"
                    @dragenter.prevent="dragEnterDropzone"
                    @dragleave.prevent="dragLeaveDropzone"
                    @dragend.prevent
                    @dragover.prevent
                  >
                    <i class="far fa-spinner fa-spin" v-if="fileIsUploading"></i>
                    Drop file here
                  </div>
                </div>
              </template>

              <template v-slot:sidebar>
                <div class="bi-sidebar">
                  <BoardItemStatus :status="item.boardItemStatus" size="xl" v-if="!item.isTemplate"></BoardItemStatus>

                  <board-item-date
                    v-if="!item.isTemplate"
                    :disabled="readOnly"
                    :displayPlannedDate="currentBoardShiftsState"
                    :boardItem="item"
                    v-on:boardItemDateUpdated="processBoardItemDateUpdates"
                    v-on:opened="noCloseOnBackdrop = true"
                    v-on:closed="noCloseOnBackdrop = false"
                  ></board-item-date>

                  <BoardItemAssignedUser
                    v-if="!item.isTemplate"
                    v-model="item.assignedToUser"
                    :disabled="readOnly"
                    v-on:boardItemAssignedUserUpdated="processAssignedUserUpdates"
                  ></BoardItemAssignedUser>

                  <div class="label mt-2 mb-1">Status</div>

                  <div>
                    <ToggleSwitch
                      label="High Priority"
                      :value="item.priority == 'High'"
                      :color="toggleSwitchColors.red"
                      @update="(update) => processPriorityChange(update)"
                    />

                    <ToggleSwitch
                      label="Relevant for Report"
                      v-model="item.isRelevantForReport"
                      :color="toggleSwitchColors.blue"
                      @update="(update) => processIsRelevantForReportChange(update)"
                    />
                    <ToggleSwitch
                      label="Task is confidential"
                      v-model="item.isConfidential"
                      :color="toggleSwitchColors.blue"
                      @update="(update) => processIsConfidentialChange(update)"
                    />
                  </div>

                  <div class="mt-3" v-if="item.specialOccurrenceId && item.id">
                    <div class="label">Special Occurrence</div>
                    <div class="d-flex justify-content-between">
                      <div class="badge so-badge" :class="occurrenceClass">
                        <router-link
                          :to="{
                            name: getTechnicalOccurrenceRouteName(),
                            params: { occId: item.specialOccurrenceId },
                          }"
                        >
                          {{ item.specialOccurrenceName }}
                        </router-link>
                      </div>
                      <i
                        v-if="item.specialOccurrenceCouldBeUnlinked && !readOnly"
                        class="far fa-trash-alt cursor-pointer so-remove"
                        v-on:click="moveBoardItemFromOccurrence(item.id)"
                      ></i>
                    </div>
                  </div>

                  <!-- Add Section -->
                  <div v-if="item.id && !readOnly">
                    <div class="label mt-4 mb-1">Add</div>

                    <AddLabels :item="item" />

                    <UploadAttachment :destination="'BoardItem'" :destinationId="item.id" />

                    <!-- Create Checklist -->
                    <AddChecklist :item="item"></AddChecklist>

                    <div class="mt-2">
                      <button class="btn btn-block btn-info" id="send-email" @click="addSpreadSheet()">
                        <i class="far fa-file-excel icon-left"></i>
                        Spreadsheet
                      </button>
                    </div>

                    <!-- Special Occurrence -->
                    <div
                      class="mt-2"
                      v-b-tooltip.hover.right
                      :title="item.id ? '' : 'Please save this task, before linking it to Special Occurrence'"
                      v-if="anyOccurreceIsAvailable && !item.specialOccurrenceId && !item.isTemplate"
                    >
                      <button :disabled="!item.id || readOnly" class="btn btn-block btn-info" id="link-to-so" @click="soSearch = ''">
                        <i class="far fa-star-exclamation icon-left"></i>
                        Special Occurrence
                      </button>

                      <b-popover ref="link-to-so" target="link-to-so" triggers="click blur" custom-class="so-popover">
                        <div class="label mt-1 mb-1 d-flex align-items-center justify-content-center">Special Occurrence</div>
                        <div>
                          <input type="text" v-model="soSearch" class="form-control" placeholder="Search..." aria-label="soSearch" />
                        </div>

                        <div class="label mt-2 mb-1 d-flex align-items-center justify-content-center">Active Special Occurrences</div>
                        <hr class="mt-2" />

                        <div v-if="filteredActiveOccurrences.length">
                          <div v-for="occ in filteredActiveOccurrences" :key="occ.id" class="bi-popover-item">
                            <button class="btn btn-block btn-info" @click="linkBoardItem(occ)">
                              {{ occ.name }}
                            </button>
                          </div>
                        </div>
                        <div v-else class="label mt-1 mb-1 d-flex align-items-center justify-content-center">
                          No Active Occurrences available
                        </div>

                        <hr class="mt-3" />
                        <div class="label mt-1 mb-1 d-flex align-items-center justify-content-center">Archived Special Occurrences</div>
                        <hr class="mt-2" />

                        <div v-if="filteredArchivedOccurrences.length">
                          <div v-for="occ in filteredArchivedOccurrences" :key="occ.id" class="bi-popover-item">
                            <button class="btn btn-block btn-info" @click="linkBoardItem(occ)">
                              {{ occ.name }}
                            </button>
                          </div>
                        </div>
                        <div v-else class="label mt-1 mb-1 d-flex align-items-center justify-content-center">
                          No Archived Occurrences available
                        </div>
                      </b-popover>
                    </div>
                    <!-- Special Occurrence End-->

                    <!-- Template Pre-Sharing -->
                    <!-- <div v-if="item.isTemplate && item.isWideItem" class="mt-2" v-b-tooltip.hover.right>
                      <button class="btn btn-block btn-info" id="template-share-with">
                        <i class="far fa-share icon-left"></i>
                        <span v-if="!item.isShared">Share task</span>
                        <span v-else>Share settings</span>
                      </button>

                      <b-popover ref="template-shareWith" target="template-share-with" triggers="click blur">
                        <div class="label mt-1 mb-1 d-flex align-items-center justify-content-center" v-if="boardsToShare.length">
                          Share with...
                        </div>
                        <div v-for="board in boardsToShare" :key="board.id" class="bi-popover-item">
                          <button class="btn btn-block btn-info d-flex align-items-center" @click="shareWith(board, true)">
                            <i class="far fa-plus-circle mr-2"></i>
                            {{ board.title }}
                          </button>
                        </div>

                        <div class="label mt-1 mb-1 d-flex align-items-center justify-content-center" v-if="sharedBoards.length">
                          Shared with...
                        </div>
                        <div v-for="board in sharedBoards" :key="board.id" class="bi-popover-item">
                          <button class="btn btn-block btn-success d-flex align-items-center" @click="unshare(board, true)">
                            <i class="far fa-minus-circle mr-2"></i>
                            {{ board.title }}
                          </button>
                        </div>
                      </b-popover>
                    </div> -->
                    <!-- Template Pre-Sharing End-->
                  </div>

                  <!--Collaboration Start-->
                  <div v-if="item.id && !item.isTemplate && !readOnly">
                    <div class="label mt-4 mb-1 d-flex justify-content-between">
                      <span>Collaboration</span>
                    </div>

                    <div class="mt-2">
                      <button class="btn btn-block btn-info" id="send-email" @click="openSendAsEmail()">
                        <i class="far fa-envelope icon-left"></i>
                        Send as email
                      </button>
                    </div>

                    <!-- Share with -->
                    <div
                      class="mt-2"
                      v-b-tooltip.hover.right
                      :title="item.id ? '' : 'Please save this task, before sharing it with another board'"
                      v-if="allBoardsExceptCurrent.length && !item.isDelegated && !item.isConfidential"
                    >
                      <button :disabled="!item.id" class="btn btn-block btn-info" id="share-with">
                        <i class="far fa-share icon-left"></i>
                        <span v-if="!item.isShared">Share task</span>
                        <span v-if="item.isShared">Share settings</span>
                      </button>

                      <b-popover ref="shareWith" target="share-with" triggers="click blur">
                        
                        <div class="label mt-1 mb-1 d-flex align-items-center justify-content-center" v-if="boardsToShare.length">
                          Share with...
                        </div>
                        <div v-for="board in boardsToShare" :key="board.id" class="bi-popover-item">
                          <button class="btn btn-block btn-info d-flex align-items-center" @click="shareWith(board)">
                            <i class="far fa-plus-circle mr-2"></i>
                            {{ board.title }}
                          </button>
                        </div>

                        <div class="label mt-1 mb-1 d-flex align-items-center justify-content-center" v-if="sharedBoards.length">
                          Shared with...
                        </div>
                        <div v-for="board in sharedBoards" :key="board.id" class="bi-popover-item">
                          <button class="btn btn-block btn-success d-flex align-items-center" @click="unshare(board)">
                            <i class="far fa-minus-circle mr-2"></i>
                            {{ board.title }}
                          </button>
                        </div>

                        <template v-if="item.isShared">
                          <button class="btn btn-block btn-danger d-flex align-items-center" @click="leaveSharedItem">
                            <i class="far fa-minus-circle mr-2"></i>
                            Remove {{ currentBoardName }} from shared task
                          </button>

                          <button
                            class="btn btn-block btn-danger d-flex align-items-center"
                            @click="stopSharing"
                            v-b-tooltip.bottom="'Terminates sharing and moves the task to your current board to the Tasks column'"
                          >
                            <i class="far fa-minus-circle mr-2"></i>
                            End (terminate) task sharing
                          </button>
                        </template>
                      </b-popover>
                    </div>

                    <!-- Move to -->
                    <div
                      class="mt-2"
                      v-b-tooltip.hover.right
                      :title="item.id ? '' : 'Please save this task, before moving it to another board'"
                      v-if="allBoardsExceptCurrent.length && !item.isDelegated && !item.isShared && !item.isConfidential"
                    >
                      <button :disabled="!item.id" class="btn btn-block btn-info" id="move-to">
                        <i class="far fa-share icon-left"></i>
                        Move to
                      </button>

                      <b-popover ref="moveTo" target="move-to" triggers="click blur">
                        <div class="label mt-1 mb-1 d-flex align-items-center justify-content-center">Move to...</div>

                        <div v-for="board in allBoardsExceptCurrent" :key="board.id" class="bi-popover-item">
                          <button class="btn btn-block btn-info" @click="moveTo(board)">
                            {{ board.title }}
                          </button>
                        </div>
                      </b-popover>
                    </div>

                    <!-- Delegate to -->
                    <div
                      class="mt-2"
                      v-b-tooltip.hover.right
                      :title="item.id ? '' : 'Please save this task, before delegating it to another board'"
                      v-if="allBoardsExceptCurrent.length && !item.isDelegated && !item.isShared && !item.isConfidential"
                    >
                      <button :disabled="!item.id" class="btn btn-block btn-info" id="delegate-to">
                        <i class="far fa-share icon-left"></i>
                        Delegate to
                      </button>

                      <b-popover ref="delegateTo" target="delegate-to" triggers="click blur">
                        <div class="label mt-1 mb-1 d-flex align-items-center justify-content-center">Delegate to...</div>

                        <div v-for="board in allBoardsExceptCurrent" :key="board.id" class="bi-popover-item">
                          <button class="btn btn-block btn-info" @click="delegate(board)">
                            {{ board.title }}
                          </button>
                        </div>
                      </b-popover>
                    </div>

                    <!-- Reject / Give Back -->
                    <div v-if="item.isDelegated && item.ownerBoardId !== boardStore.currentBoardId">
                      <div class="mt-2">
                        <button class="btn btn-block btn-info" @click="giveBackDelegatedBoardItem()">
                          <i class="fas fa-share icon-left"></i>
                          Reject / Give Back
                        </button>
                      </div>
                    </div>

                    <!-- Take Back -->
                    <div
                      v-if="
                        item.isDelegated && item.ownerBoardId === boardStore.currentBoardId && item.boardItemStatus === itemStatus.Planned
                      "
                    >
                      <div class="mt-2">
                        <button class="btn btn-block btn-info" @click="takeBackDelegatedBoardItem()">
                          <i class="fas fa-share icon-left"></i>
                          Take Back
                        </button>
                      </div>
                    </div>
                  </div>

                  <BoardItemAcceptReject :item="item" :view="acceptRejectView.Vertical"></BoardItemAcceptReject>

                  <template v-if="!readOnly">
                    <button
                      v-if="item.id && !item.isTemplate"
                      class="mt-2 btn btn-block btn-info"
                      id="toggle-watch"
                      @click="toggleSubscription()"
                    >
                      <i
                        class="far icon-left"
                        :class="{ 'fa-eye-slash': item.currentUserIsSubscribed, 'fa-eye': !item.currentUserIsSubscribed }"
                      ></i>
                      <span v-if="item.currentUserIsSubscribed">Unsubscribe</span>
                      <span v-else>Subscribe</span>
                    </button>
                  </template>

                  <!--Collaboration End -->

                  <!-- Board Item report   -->
                  <button
                    class="mt-4 btn btn-block btn-info"
                    v-if="!item.attachments?.length && item.id"
                    :disabled="pdfIsLoading"
                    @click="getBoardItemReport()"
                  >
                    <i v-if="!pdfIsLoading" class="far fa-file-pdf icon-left"></i>
                    <i v-else class="far fa-spinner fa-spin icon-left"></i>
                    Generate Report
                  </button>

                  <button
                    class="mt-2 btn btn-block btn-info"
                    v-show="item.attachments?.length && item.id"
                    id="generate-report"
                    :disabled="pdfIsLoading"
                  >
                    <i v-if="!pdfIsLoading" class="far fa-file-pdf icon-left"></i>
                    <i v-else class="far fa-spinner fa-spin icon-left"></i>
                    Generate Report
                  </button>

                  <div v-if="item.id" class="mt-2" v-b-tooltip.hover.right>
                    <button class="btn btn-block btn-info" id="bi-advanced-actions">
                      <i class="far fa-share icon-left"></i>
                      Advanced
                    </button>

                    <b-popover ref="bi-advanced-actions" target="bi-advanced-actions" triggers="click blur">
                      <button
                        class="mt-2 btn btn-block btn-info bi-popover-item"
                        v-if="currentUserIsBoardAdmin && !item.isTemplate"
                        @click="createTemplateFromBoardItem"
                      >
                        <i class="far fa-star-exclamation icon-left"></i>
                        Create Template from Task
                      </button>

                      <button
                        v-if="item.id && !item.isTemplate"
                        class="mt-2 btn btn-block btn-info bi-popover-item"
                        id="toggle-watch"
                        @click="toggleAutomaticStatusChangeOnShiftHandover()"
                      >
                        <i class="far icon-left fa-cog mr-1"></i>
                        <span v-if="item.enableAutomaticStatusChangeOnShiftHandover">Auto-Status change: ON</span>
                        <span v-else>Auto-Status change: OFF</span>
                        <i
                          class="ml-2 far fa-info-circle"
                          v-b-tooltip="
                            'Toggle if this task will be automatically moved to the Tasks list on Shift Handover if it is still in progress'
                          "
                        ></i>
                      </button>

                      <BoardItemHistory :boardItem="item" v-if="!item.isTemplate" class="bi-popover-item"></BoardItemHistory>
                    </b-popover>
                  </div>

                  <b-popover ref="generateReportPopover" target="generate-report" triggers="click blur">
                    <button class="btn btn-block btn-info d-flex align-items-center" :disabled="pdfIsLoading" @click="getBoardItemReport()">
                      <i v-if="!pdfIsLoading" class="far fa-file-pdf mr-2"></i>
                      <i v-else class="far fa-spinner fa-spin mr-2"></i>
                      PDF only
                    </button>

                    <button
                      class="btn btn-block btn-info d-flex align-items-center"
                      :disabled="pdfIsLoading"
                      @click="getBoardItemReport(true)"
                    >
                      <i v-if="!pdfIsLoading" class="far fa-file-pdf mr-2"></i>
                      <i v-else class="far fa-spinner fa-spin mr-2"></i>
                      ZIP with PDF and Attachments
                    </button>
                  </b-popover>

                  <!-- Save new BoardItem -->
                  <div v-if="!item.id">
                    <div class="label mt-4 mb-1">Save new Task</div>
                    <div class="label mb-2">
                      <button
                        class="btn btn-block btn-sm btn-success"
                        :disabled="isSaving || !item.boardItemTypeId || !item.title"
                        @click="
                          isSaving = true;
                          addNewBoardItemAndOpen();
                        "
                      >
                        <i class="far fa-save"></i>
                        Save changes
                      </button>
                    </div>
                    <div class="label mb-2">
                      <button class="btn btn-block btn-sm btn-primary" @click="addNewBoardItemAndClose()" :disabled="invalid || !isDirty">
                        <i class="far fa-save"></i>
                        Save and close
                      </button>
                    </div>
                  </div>

                  <!-- Update BoardItem -->
                  <div v-if="item.id && !readOnly" class="mt-4">
                    <div class="label mb-2">
                      <button class="btn btn-block btn-sm btn-success" @click="saveChanges()" :disabled="invalid || !isDirty">
                        <i class="far fa-save"></i>
                        Save changes
                      </button>
                    </div>

                    <div class="label mb-2">
                      <button class="btn btn-block btn-sm btn-primary" @click="saveChangesAndClose()" :disabled="invalid || !isDirty">
                        <i class="far fa-save"></i>
                        Save and close
                      </button>
                    </div>
                  </div>
                </div>
              </template>
            </modal-window>
          </ValidationObserver>
        </div>
      </div>
    </b-modal>

    <b-modal ref="unsavedValidModal" hide-footer body-class="modal-body-wo" centered hide-header>
      <modal-window :mainWidth="12">
        <div class="d-block">
          <h1>
            <i class="far fa-exclamation-triangle"></i>
            Unsaved changes
          </h1>
          <p>You have unsaved changes. Do you want to go back or discard the changes?</p>
        </div>
        <hr class="mt-3" />
        <div class="mt-3 d-flex justify-content-end">
          <button
            class="mr-2 btn btn-danger btn-sm"
            @click="
              $refs['unsavedValidModal'].hide();
              discardChanges();
            "
          >
            Discard changes and close
          </button>
          <button class="mr-2 btn btn-primary btn-sm" @click="$refs['unsavedValidModal'].hide()">Cancel</button>
        </div>
      </modal-window>
    </b-modal>

    <b-modal ref="unsavedInValidModal" hide-footer hide-header body-class="modal-body-wo" centered>
      <modal-window :main-width="12">
        <div class="d-block">
          <h1>
            <i class="far fa-exclamation-triangle"></i>
            Validation Error
          </h1>
          <p>
            Your input is not valid. Please fill the form correctly or return to the board if you do not want to create a new BoardItem.
          </p>
        </div>
        <hr class="mt-3" />
        <div class="mt-3 d-flex justify-content-end">
          <button class="mr-2 btn btn-danger btn-sm" @click="discardChanges()">
            Return to {{ metadata.occId ? 'Occurrence' : 'Board' }}
          </button>
          <button class="btn btn-primary btn-sm" @click="$refs['unsavedInValidModal'].hide()">Stay and fill</button>
        </div>
      </modal-window>
    </b-modal>

    <b-modal
      ref="sendAsEmailModal"
      id="sendAsEmailModal"
      hide-footer
      hide-header
      body-class="modal-body-wo"
      size="lg"
      :no-close-on-backdrop="true"
    >
      <send-as-email :item="item"></send-as-email>
    </b-modal>

    <b-modal
      ref="getBoardItemReport"
      id="getBoardItemReport"
      hide-footer
      hide-header
      body-class="modal-body-wo"
      size="lg"
      :no-close-on-backdrop="true"
    >
      <board-item-report :item="item"></board-item-report>
    </b-modal>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue';
  import Component from 'vue-class-component';
  import { sortBy } from 'lodash';
  import { BoardModule, BoardStore } from '@/store/board';
  import { UserModule, UserStore } from '@/store/user';
  import { ConfirmModal } from '@/helpers/confirm-modal';
  import {
    IBoardItemDto,
    BoardItemStatusDto,
    BoardItemPriorityDto,
    MoveBackTypeDto,
    AttachmentDestination,
    IAttachmentDto,
    TechnicalOccurrenceViewDto,
    BoardItemShareDto,
    BoardItemUnshareDto,
    BoardItemDto,
    IBoardDto,
    BoardItemClickDto,
  } from '@/apiclient/apiclient_generated';
  import ModalWindow from '@/components/ModalWindow.vue';
  import BoardItemConnections from '@/components/BoardItem/BoardItemConnections.vue';
  import BoardItemStatus from '@/components/BoardItem/BoardItemStatus.vue';
  import BoardItemHistory from '@/components/BoardItem/BoardItemHistory.vue';
  import BoardItemPriority from '@/components/BoardItem/BoardItemPriority.vue';
  import BoardItemActions from '@/components/BoardItemActions/BoardItemActions.vue';
  import BoardItemDate from '@/components/BoardItem/BoardItemDate.vue';
  import LoadingSpinner from '@/components/Misc/LoadingSpinner.vue';
  import IBoardItemDates from '@/types/boardItemDates';
  import GroupedSelect from '@/components/Misc/GroupedSelect.vue';
  import ToggleSwitch from '@/components/Misc/ToggleSwitch.vue';
  import AcceptRejectView from '@/types/acceptRejectView';
  import BoardItemAcceptReject from '@/components/BoardItem/BoardItemAcceptReject.vue';
  import BoardItemSpreadsheet from '@/components/BoardItem/BoardItemSpreadsheet.vue';
  import AddChecklist from '@/components/Checklist/AddChecklist.vue';
  import AddLabels from '@/components/Labels/AddLabels.vue';
  import Checklist from '@/components/Checklist/Checklist.vue';
  import WideAttachments from '@/components/Attachments/WideAttachments.vue';
  import RouteNames from '@/types/routeNames';
  import ToggleSwitchColors from '@/types/toggleSwitchColors';
  import { SpecialOccurrenceModule, SpecialOccurrenceStore } from '@/store/specialOccurrence';
  import BoardItemLabels from '@/components/Labels/BoardItemLabels.vue';
  import UploadAttachment from '@/components/Attachments/UploadAttachment.vue';
  import AttachmentView from '@/components/Attachments/AttachmentView.vue';
  import { LabelModule, LabelStore } from '@/store/label';
  import SendAsEmail from '@/components/SendAsEmail.vue';
  import BoardItemAssignedUser from '@/components/BoardItem/BoardItemAssignedUser.vue';
  import { SettingsModule, SettingsStore } from '@/store/settings';
  import { SelectListItemGroup } from '@/components/Misc/SelectListItemGroup';
  import { AttachmentsModule, AttachmentsStore } from '@/store/attachments';
  import { GlobalHelper } from '@/helpers/global-helper';
  import TaskDetailComments from '@/components/TaskDetail/TaskDetailComments.vue';

  import { reportsClient } from '@/apiclient/opsboardapi';

  import { linkify } from '@/directives/linkify-directive';
  import WysiwigEditor from '@/components/Misc/WysiwigEditor.vue';
  import BoardItemSwitcher from '../BoardItem/BoardItemSwitcher.vue';
  import SpecialOccurrenceService from '@/services/specialOccurrence.service';
  import FileService from '@/services/files.service';

  Component.registerHooks(['beforeRouteUpdate']);

  @Component({
    components: {
      WysiwigEditor,
      TaskDetailComments,
      BoardItemAssignedUser,
      SendAsEmail,
      LoadingSpinner,
      ModalWindow,
      BoardItemStatus,
      BoardItemHistory,
      BoardItemDate,
      GroupedSelect,
      BoardItemConnections,
      BoardItemPriority,
      BoardItemActions,
      BoardItemAcceptReject,
      AddChecklist,
      Checklist,
      AddLabels,
      BoardItemLabels,
      UploadAttachment,
      AttachmentView,
      BoardItemSwitcher,
      WideAttachments,
      ToggleSwitch,
      BoardItemSpreadsheet,
    },
  })
  export default class TaskDetail extends Vue {
    private labelStore: LabelModule = LabelStore;
    private userStore: UserModule = UserStore;
    private boardStore: BoardModule = BoardStore;
    private occurrenceStore: SpecialOccurrenceModule = SpecialOccurrenceStore;
    private settingsStore: SettingsModule = SettingsStore;
    private attachmentsStore: AttachmentsModule = AttachmentsStore;
    private routes: RouteNames = RouteNames;
    private acceptRejectView = AcceptRejectView;
    private commentIsDirty = false;

    private jsonEditorOptions = {
      mode: 'code',
    };

    private updatesStore = [];
    private updateStoreKeys = {
      assignedUser: 'assignedUser',
      validFromDate: 'validFromDate',
      validToDate: 'validToDate',
      dueDate: 'dueDate',
      plannedDate: 'plannedDate',
      priority: 'priority',
      isRelevantForReport: 'isRelevantForReport',
      isConfidential: 'isConfidential',
      spreadsheetUpdate: 'spreadsheetUpdate',
    };
    private allowModalClose = false;

    private comment = '';
    editdescription = false;
    isSaving = false;
    toggleActive = false;
    showBoardItemSpreadsheet = false;

    private itemid = '';
    private loading = true;
    pdfIsLoading = false;
    private noCloseOnBackdrop = false;
    private itemStatus = BoardItemStatusDto;
    toggleSwitchColors = ToggleSwitchColors;

    boardItemsChanges = {};

    private soSearch = '';

    private metadata = {
      initialOwnerBoardId: null,
      occId: null,
    };

    private initMetadata() {
      this.metadata.initialOwnerBoardId = this.$route.params.board;
      this.metadata.occId = this.$route.params.occId;
    }

    async created() {
      this.itemid = this.getItemId();

      this.initMetadata();

      await this.occurrenceStore.getActiveAndArchivedOccurrences();

      this.$root.$on('actionDateUpdated', (event) => {
        this.setPropChanges(event.name, event.value);
      });
    }

    get actionsTemplateObj() {
      return JSON.parse(this.item.actionsJson);
    }
    set actionsTemplateObj(val) {
      this.item.actionsJson = JSON.stringify(val);
    }

    get getModalSize() {
      return 'xl';
    }

    get dynamicTemplateFilesObj() {
      if (!this.item.dynamicTemplateFilesJson) return [];

      return JSON.parse(this.item.dynamicTemplateFilesJson);
    }

    set dynamicTemplateFilesObj(val) {
      this.item.dynamicTemplateFilesJson = JSON.stringify(val);
    }

    get currentUserIsBoardAdmin() {
      return (
        this.userStore.user.isAdmin ||
        this.userStore.user.boardPermissions?.filter((_) => _.boardGuid == this.item.ownerBoardId && _.isBoardAdmin).length > 0
      );
    }

    get linkifiedDescription(): string {
      const html = linkify(this.item.description);
      return html;
    }

    get anyOccurreceIsAvailable() {
      return (
        this.occurrenceStore.activeAndArchivedOccurrences.activeSpecialOccurrences?.length ||
        this.occurrenceStore.activeAndArchivedOccurrences.archivedSpecialOccurrences?.length
      );
    }

    get occurrenceClass(): string {
      const boardItem = this.item;
      const occurrences = this.occurrenceStore.activeAndArchivedOccurrences;

      const occ =
        occurrences.activeSpecialOccurrences.find((_) => _.id == boardItem.specialOccurrenceId) ||
        occurrences.archivedSpecialOccurrences.find((_) => _.id == boardItem.specialOccurrenceId);

      if (!occ) {
        return 'badge-so';
      }

      if (occ.isCompleted) {
        return 'badge-so-completed';
      }

      if (occ.type === 'TechnicalOccurrence' && (occ as TechnicalOccurrenceViewDto).technicalOccurrenceTypeId) {
        const typeInfo = this.occurrenceStore.technicalOccurrenceTypes.find(
          (_) => _.id === (occ as TechnicalOccurrenceViewDto).technicalOccurrenceTypeId
        );

        return typeInfo?.cssClass ?? 'badge-so';
      }

      return 'badge-so';
    }

    get currentBoardName(): string {
      return this.boardStore.board.title;
    }

    private filterOccurreces(occurrences, search) {
      if (search) {
        return (occurrences || []).filter((_) => _.name.toLowerCase().indexOf(search) !== -1);
      } else {
        return occurrences;
      }
    }

    get filteredActiveOccurrences() {
      const occurrences = this.filterOccurreces(
        this.occurrenceStore.activeAndArchivedOccurrences.activeSpecialOccurrences,
        this.soSearch.toLowerCase()
      );

      return this.lodash.orderBy(occurrences, 'createdDate', 'desc');
    }

    get filteredArchivedOccurrences() {
      const occurrences = this.filterOccurreces(
        this.occurrenceStore.activeAndArchivedOccurrences.archivedSpecialOccurrences,
        this.soSearch.toLowerCase()
      );

      return this.lodash.orderBy(occurrences, 'updatedDate', 'desc');
    }

    get readOnly(): boolean {
      return this.item ? this.item.isArchived || this.item.isReadonly : false;
    }

    //gets triggered by vue router if we route from one item to another
    //Vue re-uses the already instantiated component, so we don't have a "real" navigation and
    //need to update the data manually.
    beforeRouteUpdate(to, from, next) {
      this.itemid = to.params.itemid;
      this.loadData().then(() => {
        next();
      });
    }

    get currentBoardShiftsState() {
      if (this.item.specialOccurrenceId && this.boardStore.allBoards?.length) {
        const index = this.boardStore.allBoards.findIndex((_) => _.id === this.item.initialOwnerBoardId);

        return this.boardStore.allBoards[index].enableShifts;
      }

      return this.boardStore.board.enableShifts;
    }

    async createTemplateFromBoardItem() {
      const success = async () => {
        await this.boardStore.createTemplateFromBoardItem();
      };

      ConfirmModal.confirm(
        this,
        `Are you sure you want to create a template from this task?`,
        'Confirm',
        'Yes, create template',
        'No',
        success,
        null
      );
    }

    async moveBoardItemFromOccurrence(boardItemId) {
      const success = () => {
        this.occurrenceStore.unlinkBoardItem(boardItemId);
      };

      ConfirmModal.confirm(
        this,
        `Are you sure you want to unlink this item from Special Occurrence ${this.item.specialOccurrenceName} ?`,
        'Confirm unlink',
        'Yes, unlink',
        'No',
        success,
        null
      );
    }

    get availableBoardCategories() {
      const settings = this.settingsStore.shiftValidationSettings;

      const availableTypes = settings.types?.filter((_) => _.isAvailable) ?? [];

      const result: SelectListItemGroup[] = [];

      this.boardStore.groupedSelect.forEach((category) => {
        const types = category.items.filter((_) => availableTypes.some((t) => t.typeId === _.id) || _.id == this.item.boardItemTypeId);

        if (types && types.length) {
          category.items = types;
          result.push(category);
        }
      });

      return result;
    }

    private getItemId() {
      return this.$route.params.boardItemOverviewId || this.$route.params.shiftBoardItemOverviewId || this.$route.params.itemid;
    }

    async loadData() {
      this.loading = true;
      if (this.itemid == 'new') {
        const isTemplate = this.$route.name === 'TemplateDetail';
        this.boardStore.initNewBoardItem(isTemplate);

        if (this.metadata.initialOwnerBoardId) {
          this.item.specialOccurrenceId = this.metadata.occId;
          this.item.ownerBoardId = this.metadata.initialOwnerBoardId;
          this.item.initialOwnerBoardId = this.metadata.initialOwnerBoardId;
        }
      } else {
        await this.boardStore.getById(this.itemid);
      }

      await this.boardStore.loadBoardCategories();
      await this.settingsStore.loadShiftValidationSettings(this.item?.ownerBoardId ?? this.$route.params.id); //params.id is the id of the board

      this.loading = false;

      await this.$nextTick();
      await this.$nextTick(); //why I need this twice???

      const title = this.$refs.title as any;
      if (title) title.resize();

      const description = this.$refs.description as any;
      if (description) description.resize();

      //focus title for new BoardItem
      if (!this.item.id && title) {
        title.$el.focus();
      }
    }

    async mounted() {
      this.$bvModal.show('taskdetail');
      await this.loadData();

      await this.labelStore.getAllLabelsAsync(this.item.ownerBoardId || this.$route.params.id);

      await this.boardStore.loadAllBoards();
      await this.boardStore.loadAvailableBoards();

      await this.occurrenceStore.getActiveAndArchivedOccurrences();

      if (this.item.id) {
        this.boardStore.processBoardItemView(this.getBoardItemClickDto(this.item));
      }

      this.showBoardItemSpreadsheet = !!this.item.spreadsheetData;
    }

    processSpreadsheetDelete() {
      this.showBoardItemSpreadsheet = false;
      this.processSpreadsheetUpdate(null);
    }

    addSpreadSheet() {
      if (!this.showBoardItemSpreadsheet) {
        this.showBoardItemSpreadsheet = true;
        this.processSpreadsheetUpdate(null);
      }
    }

    getBoardItemClickDto(boardItem: IBoardItemDto): BoardItemClickDto {
      const currentBoardId = this.boardStore.currentBoardId ?? this.$route.params.id;

      return {
        boardId: currentBoardId,
        boardItemId: boardItem.id,
      } as BoardItemClickDto;
    }

    async getBoardItemReport(includeAttachments = false) {
      this.pdfIsLoading = true;

      const result = await reportsClient.getBoardItemPdfReport(this.item.id, includeAttachments);

      FileService.downloadFile(result.data, result.fileName);

      this.pdfIsLoading = false;

      const popover: any = this.$refs.generateReportPopover;
      if (popover) {
        popover.doClose();
      }
    }

    get showAttachmentsSection() {
      return this.item.attachments?.length > 0 || this.item.linkedBoardItems?.some((_) => _.attachments.length > 0);
    }

    async toggleSubscription() {
      await this.boardStore.toggleSubscription(this.item.id);
      GlobalHelper.NotifySuccess('Subscription updated');
    }

    async toggleAutomaticStatusChangeOnShiftHandover() {
      await this.boardStore.toggleAutomaticStatusChange(this.item.id);
      GlobalHelper.NotifySuccess('Automatic status change updated');
    }

    async imagePasted(imageData: string | ArrayBuffer) {
      await this.attachmentsStore.addAttachmentFromBase64({
        destination: AttachmentDestination.BoardItem,
        id: this.item.id,
        imageData: imageData,
      });
    }

    modalClosed() {
      const location: any = {
        name: 'Board',
        params: {
          id: this.$route.params.id,
        },
      };

      switch (this.$route.name) {
        case RouteNames.specialOccurrence.task:
          location.name = RouteNames.specialOccurrence.taskGeneration;
          location.params.occId = this.$route.params.occId;
          break;

        case 'boardItemOverview':
          location.name = 'board-overview';
          break;
        case 'shiftBoardItemOverview':
          location.name = 'shifts-overview';
          break;
        case 'TemplateDetail':
          location.name = RouteNames.biTemplates;
          break;
        case RouteNames.specialOccurrence.attachmentitem:
          location.name = RouteNames.specialOccurrence.attachments;
          location.params.occId = this.$route.params.occId;
      }

      this.$router.push(location);
    }

    get allBoardsExceptCurrent() {
      const boards = this.boardStore.allBoardsExceptCurrent;

      if (this.item.specialOccurrenceId) {
        const index = boards.findIndex((_) => _.id === this.item.initialOwnerBoardId);

        if (index >= 0) {
          boards.splice(index, 1);
        }
      }

      return this.orderBoards(boards);
    }

    private get boardsToShare() {
      const boards = this.boardStore.allBoardsExceptCurrent;
      const sharedBoardIds = this.item.sharedBoards.map((_) => _.boardId);

      const filteredBoards = boards.filter((_) => sharedBoardIds.indexOf(_.id) === -1);

      return this.orderBoards(filteredBoards);
    }

    private orderBoards(boards: IBoardDto[]) {
      return this.lodash.orderBy(boards, (t) => t.title);
    }

    private get sharedBoards() {
      const boards = this.boardStore.allBoardsExceptCurrent;
      const sharedBoardIds = this.item.sharedBoards.map((_) => _.boardId);

      const filteredBoards = boards.filter((_) => sharedBoardIds.indexOf(_.id) !== -1);

      return this.orderBoards(filteredBoards);
    }

    private get isDirty() {
      const refs: any = this.$refs;
      const fields = refs.form?.fields || [];

      const keys = Object.keys(fields) || [];

      const isDirty = keys.some((key) => {
        const field = fields[key];

        return field.valid && field.changed;
      });

      return isDirty || this.updatesStore?.length;
    }

    async addNewBoardItemAndOpen() {
      this.allowModalClose = true;
      const newItem = await this.boardStore.addBoardItem(this.item);

      let routeName = this.metadata.occId ? RouteNames.specialOccurrence.task : 'BoardItemDetail';
      if (this.$route.name === 'TemplateDetail') routeName = 'TemplateDetail';

      const location = {
        name: routeName,
        params: { itemid: newItem.id },
      };

      this.$router.push(location);
    }

    async addNewBoardItemAndClose() {
      this.allowModalClose = true;
      await this.boardStore.addBoardItem(this.item);
      this.closeBoardItemDetails();
    }

    async saveChanges() {
      if (this.autoSaveTimeout) {
        clearTimeout(this.autoSaveTimeout);
      }

      await this.boardStore.saveBoardItem(this.item);
      const refs: any = this.$refs;
      refs.form.reset();
      this.updatesStore = [];
    }

    async saveChangesAndClose() {
      this.allowModalClose = true;
      await this.saveChanges();
      this.closeBoardItemDetails();
    }

    closeBoardItemDetails() {
      this.$root.$emit('bv::hide::modal', 'taskdetail');
    }

    saveValidChanges() {
      this.allowModalClose = true;
      this.boardStore.saveBoardItem(this.item);
      this.closeBoardItemDetails();
    }

    discardChanges() {
      this.allowModalClose = true;
      this.closeBoardItemDetails();
    }

    modalHiding(event) {
      if (this.readOnly) {
        return;
      }

      if (!this.allowModalClose) {
        event.preventDefault();
        const refs: any = this.$refs;

        refs.form.validate().then((validationResult) => {
          if (validationResult && !this.isDirty && !this.commentIsDirty) {
            this.allowModalClose = true;
            this.closeBoardItemDetails();
          } else if (validationResult && (this.isDirty || this.commentIsDirty)) {
            const modal: any = this.$refs['unsavedValidModal'];
            modal.show();
          } else if (!validationResult) {
            const modal: any = this.$refs['unsavedInValidModal'];
            modal.show();
          }
        });
      }
    }

    get item(): IBoardItemDto {
      return this.boardStore.currentBoardItem;
    }

    get orderedAttachments(): IAttachmentDto[] {
      let attachments = [];

      if (this.item.linkedBoardItems) {
        attachments = this.item.linkedBoardItems.reduce((prev, curr) => {
          curr.attachments.forEach((_: any) => {
            _.parentId = curr.id;
            _.parentTitle = curr.title;
          });

          return [...prev, ...curr.attachments];
        }, []);
      }

      if (attachments.length > 0) {
        attachments.forEach((_) => (_.isChildItem = true));
      }

      return sortBy([...this.item.attachments.filter((t) => !t.isWideBoardItemAttachment), ...attachments], 'createdDate');
    }

    async destroyed() {
      if (!this.item.boardItemTypeId && this.item.id) {
        this.boardStore.resetBoardItemType(this.item.id);
      }
    }

    async processPriorityUpdates(priority: BoardItemPriorityDto) {
      this.item.priority = priority;
      this.setPropChanges(this.updateStoreKeys.priority, priority);
    }

    processAssignedUserUpdates(userUpdates) {
      this.item.assignedToUser = userUpdates;
      this.setPropChanges(this.updateStoreKeys.assignedUser, userUpdates);
    }

    processBoardItemDateUpdates(dateUpdates: IBoardItemDates) {
      this.item.validFromDate = dateUpdates.validFromDate;
      this.item.validToDate = dateUpdates.validToDate;
      this.item.dueDate = dateUpdates.dueDate;
      this.item.plannedDate = dateUpdates.plannedDate;

      this.setPropChanges(this.updateStoreKeys.validFromDate, dateUpdates.validFromDate);
      this.setPropChanges(this.updateStoreKeys.validToDate, dateUpdates.validToDate);
      this.setPropChanges(this.updateStoreKeys.dueDate, dateUpdates.dueDate);
      this.setPropChanges(this.updateStoreKeys.plannedDate, dateUpdates.plannedDate);
    }

    processSpreadsheetUpdate(updates) {
      this.item.spreadsheetData = updates;
      this.setPropChanges(this.updateStoreKeys.spreadsheetUpdate, true);
    }

    processPriorityChange(newState) {
      this.item.priority = newState ? BoardItemPriorityDto.High : BoardItemPriorityDto.Normal;
      this.setPropChanges(this.updateStoreKeys.priority, this.item.priority);
    }

    processIsRelevantForReportChange(newState) {
      this.item.isRelevantForReport = newState;
      this.setPropChanges(this.updateStoreKeys.isRelevantForReport, newState);
    }

    processIsConfidentialChange(newState) {
      this.item.isConfidential = newState;
      this.setPropChanges(this.updateStoreKeys.isConfidential, newState);
    }

    private setPropChanges(key, value) {
      this.updatesStore.push({ key, value });
    }

    async startEditDescription() {
      if (this.readOnly) return;

      this.editdescription = true;
      await this.$nextTick();

      (this.$refs.editor as any).focus('end');
      this.noCloseOnBackdrop = true;
    }

    async leaveDescription(reason: string) {
      if (this.item.isTemplate && reason == 'blur') {
        return;
      }

      this.editdescription = false;

      setTimeout(() => {
        this.noCloseOnBackdrop = false;
      }, 200);

      await this.autoSaveDelayed();
    }

    autoSaveTimeout: any = null;

    autoSaveDelayed() {
      if (this.autoSaveTimeout) clearTimeout(this.autoSaveTimeout);

      if (!this.item.id) {
        return;
      }

      this.autoSaveTimeout = setTimeout(async () => {
        await this.saveChanges();
      }, 300);
    }

    async moveTo(board) {
      const currentBoardId = this.item.ownerBoardId;
      const newBoardId = board.id;
      const boardItemId = this.item.id;

      const success = async () => {
        await this.boardStore.moveTo({ currentBoardId, newBoardId, boardItemId });
        this.closeBoardItemDetails();
      };

      ConfirmModal.confirm(
        this,
        `Are you sure you want to move this item to ${board.title} ?`,
        'Confirm move to',
        'Yes, move',
        'No',
        success,
        null
      );
    }

    async shareWith(board, isPreSharing = false) {
      const dto = new BoardItemShareDto();
      dto.boardItemId = this.item.id;
      dto.sharedBoardId = board.id;
      dto.currentBoardId = this.boardStore.currentBoardId;

      const success = async () => {
        const biUpdates: BoardItemDto = await this.boardStore.shareWith(dto);
        if (isPreSharing) {
          this.boardStore.setCurrentBoardItem(biUpdates);
          GlobalHelper.NotifySuccess('Board item was pre-shared.');
        } else {
          this.closeBoardItemDetails();
        }
      };
      const action = isPreSharing ? 'pre-share' : 'share';

      ConfirmModal.confirm(
        this,
        `Are you sure you want to ${action} this item with ${board.title} ?`,
        'Confirm sharing',
        `Yes, ${action}`,
        'No',
        success,
        null
      );
    }

    async unshare(board, isPreSharing = false) {
      const dto = new BoardItemUnshareDto();
      dto.boardItemId = this.item.id;
      dto.currentBoardId = this.boardStore.currentBoardId;
      dto.unsharedBoardId = board.id;

      const success = async () => {
        const biUpdates = await this.boardStore.unShareWith(dto);

        if (isPreSharing) {
          this.boardStore.setCurrentBoardItem(biUpdates);
          GlobalHelper.NotifySuccess('Board item was unshared.');
        } else {
          this.closeBoardItemDetails();
        }
      };

      const action = isPreSharing ? 'pre-sharing' : 'sharing';

      ConfirmModal.confirm(
        this,
        `Are you sure you want to stop ${action} this item with ${board.title} ?`,
        `Confirm stop ${action}`,
        `Yes, stop ${action}`,
        'No',
        success,
        null
      );
    }

    async leaveSharedItem() {
      const dto = new BoardItemUnshareDto();
      dto.boardItemId = this.item.id;
      dto.currentBoardId = this.boardStore.currentBoardId;

      const success = async () => {
        await this.boardStore.leaveSharing(dto);
        this.closeBoardItemDetails();
      };

      ConfirmModal.confirm(this, `Are you sure you want to leave this task?`, 'Confirm leave task', 'Yes, leave task', 'No', success, null);
    }

    async stopSharing() {
      const currentBoardId = this.boardStore.currentBoardId;
      const newBoardId = this.boardStore.currentBoardId;
      const boardItemId = this.item.id;

      const success = () => {
        this.boardStore.stopSharing({ currentBoardId, newBoardId, boardItemId });
        this.closeBoardItemDetails();
      };

      ConfirmModal.confirm(
        this,
        `Are you sure you want to stop sharing this item for all other boards?`,
        `Confirm stop sharing`,
        `Yes, stop sharing`,
        'No',
        success,
        null
      );
    }

    async linkBoardItem(occurrence) {
      const boardItemId = this.item.id;
      const params = { occurrenceId: occurrence.id, boardItemId };

      const success = () => {
        this.occurrenceStore.linkBoardItem(params);
      };

      ConfirmModal.confirm(
        this,
        `Are you sure you want to link this item to Special Occurrence - ${occurrence.name} ?`,
        'Confirm link',
        'Yes, link',
        'No',
        success,
        null
      );
    }

    async delegate(board) {
      const currentBoardId = this.boardStore.currentBoardId;
      const newBoardId = board.id;
      const boardItemId = this.item.id;

      const success = () => {
        this.boardStore.delegate({ currentBoardId, newBoardId, boardItemId });
        this.closeBoardItemDetails();
      };

      ConfirmModal.confirm(
        this,
        `Are you sure you want to delegate this item to ${board.title} ?`,
        'Confirm delegate to',
        'Yes, delegate',
        'No',
        success,
        null
      );
    }

    async takeBackDelegatedBoardItem() {
      const params = { boardItemId: this.item.id, moveType: MoveBackTypeDto.TakeBack };

      await this.boardStore.moveBack(params);
      this.closeBoardItemDetails();
    }

    async giveBackDelegatedBoardItem() {
      const params = { boardItemId: this.item.id, moveType: MoveBackTypeDto.GiveBack };

      const success = () => {
        this.boardStore.moveBack(params);
        this.closeBoardItemDetails();
      };

      const board = this.boardStore.allBoards.find((_) => _.id === this.item.ownerBoardId);

      ConfirmModal.confirm(
        this,
        `Are you sure you want to give back this item to ${board.title} ?`,
        'Confirm give back',
        'Yes, give back',
        'No',
        success,
        null
      );
    }

    openSendAsEmail() {
      this.$bvModal.show('sendAsEmailModal');
    }

    // Drag and drop of files
    private dragCounterDropzone = 0;
    private dragOverDropzone = false;
    private draggedOver = false;
    private fileIsUploading = false;

    async dropDropzone(e) {
      if (this.fileIsUploading) return;

      e.preventDefault();
      const droppedFiles = e.dataTransfer.files;

      if (droppedFiles.length > 1) {
        alert('Please drop only one file at a time');
        this.dragCounterDropzone = 0;
        this.draggedOver = false;
        this.dragOverDropzone = false;
        return;
      }

      const file = droppedFiles[0];
      this.fileIsUploading = true;
      await this.attachmentsStore.addAttachment({
        destination: 'BoardItem',
        id: this.item.id,
        fileData: file,
      });

      this.fileIsUploading = false;
      this.dragOverDropzone = false;
      this.draggedOver = false;

      GlobalHelper.NotifySuccess('File uploaded');
    }

    dragEnterDropzone(e) {
      e.preventDefault();
      console.log(e);
      this.dragCounterDropzone++;
      this.dragOverDropzone = true;
    }

    dragLeaveDropzone(e) {
      e.preventDefault();
      this.dragCounterDropzone--;

      if (this.dragCounterDropzone == 0) this.dragOverDropzone = false;
    }

    dragEnter(e) {
      e.preventDefault();

      if (this.item.id && e.dataTransfer && e.dataTransfer.files) {
        this.draggedOver = true;
      }
    }

    dragLeave(e) {
      e.preventDefault();
      this.draggedOver = false;
    }

    getTechnicalOccurrenceRouteName() {
      const so = this.occurrenceStore.allOccurrences.find((t) => t.id === this.item.specialOccurrenceId);

      return SpecialOccurrenceService.getTechnicalOccurrenceRouteName(so);
    }
  }
</script>

<style scoped lang="scss">
  .archived-bi-details {
    background: white;
    border: 1px solid #ccc;

    > h2 {
      padding: 0.5rem;
      margin: 0;
      padding-left: 1.4rem;
      color: #888;
      font-size: 1.4rem;
    }
  }

  .striped-header {
    color: white;
    background: repeating-linear-gradient(45deg, #eaeaea, #eaeaea 10px, #d6d6d6 10px, #d6d6d6 20px);
  }

  .so-remove {
    margin-top: 4px;
    margin-left: 5px;
    font-size: 1.2rem;
    color: red;
  }

  .so-badge {
    width: 100%;
    font-size: 1.1rem;

    a {
      color: inherit;
    }
  }

  .bi-sidebar {
    flex-grow: 1;
  }

  .has-error {
    border: 1px solid red;
    border-radius: 5px;
  }

  .v-select {
    background: white;
  }

  .taskdetail {
    .task-title {
      font-weight: bold;
    }

    .area {
      width: 100%;

      &:not(:focus) {
        border: none;
        background: inherit;
      }
    }

    @keyframes highlight {
      0% {
        background-color: yellow;
      }
      100% {
        background-color: white;
      }
    }
  }

  .bi-popover-item {
    margin-bottom: 0.4em;
    min-width: 100px;
  }

  .board-item-title-error {
    height: 24px !important;
    line-height: 22px !important;
  }

  .description {
    overflow-wrap: break-word;

    ::v-deep table {
      width: 100%;
      border-collapse: collapse;
    }

    ::v-deep td {
      border-width: 1px;
      border-style: solid;
      border-color: rgb(171, 171, 171);
      box-sizing: border-box;
      word-break: break-word;
      white-space: normal;
      background-color: transparent;
    }

    ::v-deep th {
      border-width: 1px;
      border-style: solid;
      border-color: rgb(171, 171, 171);
      box-sizing: border-box;
      word-break: break-word;
      white-space: normal;
      background-color: transparent;
    }

    ::v-deep p {
      margin: 0;

      &:empty:before {
        content: ' ';
        white-space: pre;
      }
    }
  }
</style>

<style lang="scss">
  .el-tiptap-editor__content {
    padding: 10px !important;
  }

  .drop-zone-text {
    font-size: 34px;
    pointer-events: none;
  }
</style>
